import { FC, useEffect, useState } from "react";
import { Project } from "../../models";
import { NewProject } from "../../components/modals/NewProject";
import { ProjectInCard } from "../../components/Cards/ProjectInCard";
import { HorizontalStack } from "../../components/Stack";
import {
  FontSizes,
  IButtonStyles,
  mergeStyles,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { getAllProjects, getSearchedProjects } from "../redux/Projects/project";
import InfiniteScroll from "react-infinite-scroll-component";
import { ProjectService } from "../../services/ProjectService";
import useIsSignedIn from "../../hooks/useIsSignedIn";

export const AllProjectData: FC = () => {
  const isSearchEnabled = useAppSelector(
    (state) => state.isSearchEnabled.value
  );
  const searchParam = useAppSelector((state) => state.isSearchEnabled.text);
  const [projectLimit, setProjectLimit] = useState<number>(0);
  const { isLoading, data, error, metadata } = useAppSelector((state) =>
    isSearchEnabled ? state.fetchSearchedProject : state.fetchProjects
  );
  const [projectData, setProjectData] = useState<Project[]>([]);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isSignedIn = useIsSignedIn();

  const fetchProjectsLimits = async () => {
    try {
      const limit: any = await ProjectService.getProjectLimit();
      setProjectLimit(limit.data[0].value);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  useEffect(() => {
    if (!isSearchEnabled) {
      dispatch(getAllProjects({ limit: 500, offset: 0 }));
    }
  }, [isSearchEnabled, dispatch]);

  useEffect(() => {
    if (isSignedIn) {
      fetchProjectsLimits();
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (!isLoading) {
      if (data && data.length > 0) {
        setProjectData(data);
      } else {
        setProjectData([]);
      }
    }
  }, [isLoading, data]);

  useEffect(() => {
    isSearchEnabled &&
      dispatch(
        getSearchedProjects({
          projectName: searchParam,
          limit: 120,
          offset: 0,
        })
      );
  }, [isSearchEnabled, searchParam, dispatch]);

  const loaderJSX = isLoading && (
    <Stack className={mergeStyles(styles.loaderStyles)}>
      <Loader />
      <Text style={styles.loadingText}>{t("loading")}</Text>
    </Stack>
  );

  const errorJSX =
    !isLoading && error === "Network Error" ? (
      <Stack className={mergeStyles(styles.errorStyles)}>
        <Text className={mergeStyles(styles.textStyles)}>{t("loader")}</Text>
        <PrimaryButton
          styles={retryButtonStyles}
          onClick={() => dispatch(getAllProjects({ limit: 500, offset: 0 }))}
        >
          {t("retry")}
        </PrimaryButton>
      </Stack>
    ) : (
      !isLoading &&
      projectData.length === 0 && (
        <Stack className={mergeStyles(styles.errorStyles)}>
          <Text className={mergeStyles(styles.textStyles)}>
            {t("no_projects_found")}
          </Text>
        </Stack>
      )
    );

  const fetchMoreData = () => {
    let newOffSet = metadata.offset + metadata.limit;
    let searchPayload = {
      projectName: searchParam,
      limit: 20,
      offset: newOffSet,
    };
    let payload = {
      limit: 20,
      offset: newOffSet,
    };

    if (metadata.offset < metadata.count) {
      dispatch(
        isSearchEnabled
          ? getSearchedProjects(searchPayload)
          : getAllProjects(payload)
      ).then((res: any) => {
        if (res.payload.data && res.payload.data.length > 0) {
          setProjectData([...projectData, ...res.payload.data]);
        }
      });
    }
  };

  return (
    <div style={styles.ProjectStackDivStyle}>
      <HorizontalStack className={mergeStyles(styles.ProjectStackStyles)}>
        {projectData.length === 0 && projectLimit !== 0 && (
          <NewProject
            projectLimit={projectLimit}
            setProjectLimit={setProjectLimit}
          />
        )}
        {errorJSX}
        {!errorJSX && projectData.length !== 0 && (
          <InfiniteScroll
            dataLength={projectData.length}
            next={fetchMoreData}
            hasMore={projectData.length < metadata?.count}
            loader={
              <Stack
                style={{
                  padding: "5%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loader />
              </Stack>
            }
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              backgroundColor: "",
              flexWrap: "wrap",
              overflowY: "hidden",
              gap: "13px",
            }}
          >
            {projectLimit !== 0 && (
              <NewProject
                projectLimit={projectLimit}
                setProjectLimit={setProjectLimit}
              />
            )}
            {loaderJSX
              ? loaderJSX
              : projectData.map((project) => {
                  return (
                    <ProjectInCard
                      key={project.id}
                      project={project}
                      disableValueEdit
                    />
                  );
                })}
          </InfiniteScroll>
        )}
      </HorizontalStack>
    </div>
  );
};

const styles = {
  loaderStyles: {
    display: "flex",
    justifyContent: "center",
    width: "75%",
    height: "18rem",
    alignItems: "center",
    marginTop: "1%",
    gap: "10px",
  },
  textStyles: {
    fontSize: FontSizes.size20,
    fontFamily: "Poppins",
  },
  errorStyles: {
    display: "flex",
    justifyContent: "center",
    width: "75%",
    height: "18rem",
    alignItems: "center",
  },
  ProjectStackDivStyle: {
    width: "100%",
  },
  ProjectStackStyles: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    marginBottom: "20px",
  },
  loadingText: {
    fontSize: "20px",
    fontFamily: "Poppins",
  },
};

const retryButtonStyles: IButtonStyles = {
  root: {
    width: "20px",
    margin: "1%",
    alignItems: "center",
  },
};
